import { MagnifyingGlass, X } from "@phosphor-icons/react";
import { SPORTS_LIST, TEAMLESS_SPORTS, TEAMS_BY_SPORT } from "@/lib/teams";
import SellerTypeahead from "@/components/SellerTypeahead";
import IconKey from "@/components/IconKey";

const SPORTS = SPORTS_LIST;

/**
 * Persistent left-hand quick filter aside used on the Browse page.
 * Pure presentational — parent owns filter state.
 */
export default function BrowseSidebarFilters({
  filters,
  setFilters,
  facets,
  activeFiltersCount,
  resetFilters,
  yearChips,
}) {
  return (
    <aside
      className="hidden lg:block w-64 shrink-0 sticky top-20 self-start max-h-[calc(100vh-6rem)] overflow-y-auto pr-1"
      data-testid="browse-quick-filters"
    >
      <div className="font-mono text-[10px] text-[#FF5F1F] tracking-widest uppercase mb-3">→ Quick Filters</div>

      {/* Player quick search */}
      <div className="mb-6">
        <label className="h57-label">Player / Keyword</label>
        <div className="relative">
          <MagnifyingGlass size={14} weight="bold" className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500 pointer-events-none z-10" />
          <input
            data-testid="sidebar-player-search"
            className="h57-input !pl-9 !py-2 text-sm"
            placeholder="e.g. Jordan"
            value={filters.q}
            onChange={(e) => setFilters({ ...filters, q: e.target.value })}
          />
        </div>
      </div>

      {/* Seller (username) search — typeahead against /api/users/search */}
      <div className="mb-6" data-testid="sidebar-seller-wrapper">
        <label className="h57-label">Seller (@username)</label>
        <SellerTypeahead
          value={filters.seller}
          onChange={(v) => setFilters((f) => ({ ...f, seller: v }))}
        />
      </div>

      {/* Sport */}
      <div className="mb-6">
        <div className="h57-label">Sport</div>
        <div className="flex flex-wrap gap-1.5">
          {SPORTS.map((s) => {
            const count = facets.sports?.[s] || 0;
            const disabled = count === 0 && filters.sport !== s;
            return (
              <button
                key={s}
                data-testid={`sidebar-sport-${s.toLowerCase()}`}
                disabled={disabled}
                onClick={() => setFilters({ ...filters, sport: filters.sport === s ? "" : s, team: "" })}
                title={disabled ? "No active listings" : `${count} listing${count === 1 ? "" : "s"}`}
                className={`h57-chip !py-1 !px-2 !text-[10px] ${filters.sport === s ? "!border-[#FF5F1F] !text-[#FF5F1F]" : ""} ${disabled ? "opacity-30 cursor-not-allowed line-through" : ""}`}
              >{s}<span className="ml-1 opacity-60">{count}</span></button>
            );
          })}
        </div>
      </div>

      {/* Team dropdown — only when a sport with teams is selected. */}
      {filters.sport && !TEAMLESS_SPORTS.has(filters.sport) && (TEAMS_BY_SPORT[filters.sport] || []).length > 0 && (
        <div className="mb-6" data-testid="sidebar-team-wrapper">
          <div className="h57-label">Team</div>
          <select
            data-testid="sidebar-team"
            className="h57-input !py-2 text-sm"
            value={filters.team}
            onChange={(e) => setFilters({ ...filters, team: e.target.value })}
          >
            <option value="">All {filters.sport} teams</option>
            {TEAMS_BY_SPORT[filters.sport].map((t) => (
              <option key={t} value={t}>{t}</option>
            ))}
          </select>
        </div>
      )}

      {/* Card Type */}
      <div className="mb-6">
        <div className="h57-label">Card Type</div>
        <div className="flex flex-wrap gap-1.5">
          {[
            ["base", "Base"], ["insert", "Insert"], ["variation", "Variation"],
            ["rookie", "Rookie"], ["autograph", "Auto"], ["relic", "Relic"],
            ["numbered", "Numbered"], ["ssp", "SSP"], ["sp", "SP"], ["graded", "Graded"],
          ].map(([k, label]) => {
            const count = facets.types?.[k] || 0;
            const disabled = count === 0 && !filters[k];
            return (
              <button
                key={k}
                data-testid={`sidebar-type-${k}`}
                disabled={disabled}
                onClick={() => setFilters({ ...filters, [k]: !filters[k] })}
                title={disabled ? "No active listings" : `${count} listing${count === 1 ? "" : "s"}`}
                className={`h57-chip !py-1 !px-2 !text-[10px] ${filters[k] ? "!border-[#FF5F1F] !text-[#FF5F1F]" : ""} ${disabled ? "opacity-30 cursor-not-allowed line-through" : ""}`}
              >{label}<span className="ml-1 opacity-60">{count}</span></button>
            );
          })}
        </div>
      </div>

      {/* Team quick filter */}
      <div className="mb-6">
        <label className="h57-label">Team / Brand</label>
        <input
          data-testid="sidebar-brand"
          className="h57-input !py-2 text-sm"
          placeholder="Topps, Panini…"
          value={filters.brand}
          onChange={(e) => setFilters({ ...filters, brand: e.target.value })}
        />
      </div>

      {/* Year shortcuts */}
      {yearChips.length > 0 && (
        <div className="mb-6">
          <div className="h57-label">Year</div>
          <div className="flex flex-wrap gap-1.5 mb-2">
            {yearChips.map((y) => (
              <button
                key={y.v}
                data-testid={`sidebar-year-${y.v}`}
                onClick={() => setFilters({ ...filters, year: filters.year === y.v ? "" : y.v })}
                title={`${y.count} listing${y.count === 1 ? "" : "s"}`}
                className={`h57-chip !py-1 !px-2 !text-[10px] ${filters.year === y.v ? "!border-[#FF5F1F] !text-[#FF5F1F]" : ""}`}
              >{y.label}<span className="ml-1 opacity-60">{y.count}</span></button>
            ))}
          </div>
          <input
            type="number"
            data-testid="sidebar-year-custom"
            className="h57-input !py-2 text-sm"
            placeholder="Custom year"
            value={filters.year}
            onChange={(e) => setFilters({ ...filters, year: e.target.value })}
          />
        </div>
      )}

      {/* Price range */}
      <div className="mb-6">
        <div className="h57-label">Price</div>
        <div className="grid grid-cols-2 gap-2">
          <input type="number" step="0.01" data-testid="sidebar-price-min" className="h57-input !py-2 text-sm" placeholder="Min" value={filters.price_min} onChange={(e) => setFilters({ ...filters, price_min: e.target.value })} />
          <input type="number" step="0.01" data-testid="sidebar-price-max" className="h57-input !py-2 text-sm" placeholder="Max" value={filters.price_max} onChange={(e) => setFilters({ ...filters, price_max: e.target.value })} />
        </div>
      </div>

      {/* Extras */}
      <div className="mb-6">
        <button
          data-testid="sidebar-accepts-offers"
          onClick={() => setFilters({ ...filters, accepts_offers: !filters.accepts_offers })}
          className={`h57-chip !py-1.5 !px-3 w-full !text-xs ${filters.accepts_offers ? "!border-[#FF5F1F] !text-[#FF5F1F]" : ""}`}
        >Accepts Offers Only</button>
      </div>

      {activeFiltersCount > 0 && (
        <button onClick={resetFilters} data-testid="sidebar-clear" className="h57-chip !py-2 !px-3 w-full !border-red-500/30 !text-red-400 flex items-center justify-center gap-1">
          <X size={12} weight="bold" /> Clear All ({activeFiltersCount})
        </button>
      )}

      <IconKey testId="sidebar-icon-key" />
    </aside>
  );
}
